import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Building2, 
  Clock, 
  Briefcase, 
  CheckCircle, 
  ArrowRight,
  Shield,
  Zap,
  Target,
  Award,
  TrendingUp,
  FileCheck,
  Microscope,
  Cpu,
  Factory,
  Lightbulb,
  Heart,
  DollarSign,
  MapPin,
  Star,
  Upload,
  Send
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg";

const staffingTypes = [
  { icon: Clock, title: 'Temporary Staffing', description: 'Dynamic temporary staffing solutions for immediate needs and project-based requirements.' },
  { icon: TrendingUp, title: 'Temp-to-Perm Staffing', description: 'Transform temporary roles into permanent success with comprehensive temp-to-perm solutions.' },
  { icon: Users, title: 'Permanent Staffing', description: 'Find your perfect fit with premier permanent staffing solutions for long-term positions.' },
  { icon: FileCheck, title: 'Contract Staffing', description: 'Unlock flexibility and expertise with top-notch contract staffing for specialized projects.' },
  { icon: Briefcase, title: 'Direct Hire', description: 'Find your perfect permanent fit with premier direct hire solutions for key positions.' },
  { icon: Building2, title: 'Managed Services (MSP)', description: 'Transform workforce management with premier MSP solutions and strategic oversight.' },
  { icon: Cpu, title: 'Vendor Management (VMS)', description: 'Optimize staffing strategy with cutting-edge VMS solutions and technology platforms.' }
];

const lifeScienceRoles = [
  'CQV & Validation Engineering', 'Computer System Validation', 'Clinical Research Associates',
  'Regulatory Affairs Specialists', 'Quality Engineering (QA/QC)', 'Process Engineering',
  'Manufacturing Engineering', 'Technical Writing', 'Automation Engineering',
  'Cleaning Validation', 'Equipment Validation', 'Process Validation',
  'Clinical Data Management', 'Clinical SAS Programming', 'Design Quality Engineering',
  'Biomedical Engineering'
];

const partnerBenefits = [
  { icon: Shield, title: 'Pre-Vetted Professionals', description: 'Rigorously screened validation engineers, clinical researchers, and regulatory specialists with proven track records.' },
  { icon: Zap, title: 'Rapid Placement', description: 'Accelerated hiring with Corp-to-Corp and Contract-to-Hire options. Get specialized talent in weeks, not months.' },
  { icon: Award, title: 'Industry Expertise', description: 'Deep understanding of pharmaceutical, biotech, and medical device requirements including GxP compliance.' },
  { icon: Target, title: 'Specialized Matching', description: 'Precision matching for technical roles like CQV, CSV, process validation based on specific project needs.' },
  { icon: Clock, title: 'Flexible Arrangements', description: 'Corp-to-Corp and Contract-to-Hire models that align with your project timelines and budget.' },
  { icon: TrendingUp, title: 'Scalable Solutions', description: 'From single specialist placements to full validation teams for large-scale pharmaceutical projects.' }
];

const candidateBenefits = [
  { icon: TrendingUp, title: 'Career Advancement', description: 'Access senior-level positions at leading pharma and biotech companies with clear growth trajectories.' },
  { icon: DollarSign, title: 'Competitive Compensation', description: 'Corp-to-Corp and Contract-to-Hire roles offering premium rates for specialized expertise.' },
  { icon: Lightbulb, title: 'Diverse Projects', description: 'Work on cutting-edge pharmaceutical manufacturing, medical device validation, and clinical research.' },
  { icon: MapPin, title: 'Prime Locations', description: 'Opportunities in major pharmaceutical hubs and biotech corridors across the country.' },
  { icon: Award, title: 'Skill Development', description: 'Exposure to latest technologies, regulatory standards, and industry best practices.' },
  { icon: Zap, title: 'Fast Placements', description: 'Streamlined process to get you into your next role quickly with companies that value your expertise.' }
];

const industries = [
  { icon: Microscope, title: 'Pharmaceutical', subtitle: 'Large pharma, specialty pharma, and generic drug manufacturers', tags: ['Drug Manufacturing', 'API Production', 'Regulatory Compliance', 'Quality Systems'] },
  { icon: Heart, title: 'Biotechnology', subtitle: 'Emerging biotech companies and established leaders', tags: ['Cell Therapy', 'Gene Therapy', 'Biologics', 'Clinical Development'] },
  { icon: Shield, title: 'Medical Devices', subtitle: 'Medical device manufacturers from startups to global leaders', tags: ['Device Validation', 'Design Controls', 'Clinical Trials', 'FDA Submissions'] },
  { icon: Building2, title: 'Contract Organizations', subtitle: 'CROs, CDMOs, and contract manufacturing organizations', tags: ['Contract Research', 'Manufacturing Services', 'Analytical Testing', 'Regulatory Support'] },
  { icon: Cpu, title: 'Technology', subtitle: 'Software development, IT infrastructure, and emerging tech', tags: ['Software Engineering', 'Data Science', 'Cloud Architecture', 'AI/ML Development'] },
  { icon: Factory, title: 'Engineering', subtitle: 'Mechanical, electrical, chemical, and process engineering', tags: ['Process Engineering', 'Automation', 'Design Engineering', 'Project Management'] }
];

const stats = [
  { value: '500+', label: 'Professionals Placed' },
  { value: '95%', label: 'Client Satisfaction' },
  { value: '2-3', label: 'Weeks Avg Placement' },
  { value: '$120K+', label: 'Average Salary' }
];

const popularRoles = [
  'Validation Engineer', 'CQV Specialist', 'CSV Professional', 'Quality Engineer',
  'Clinical Research Associate', 'Regulatory Affairs Specialist', 'Process Engineer', 'Manufacturing Engineer'
];

export const Staffing = ({ onDemoClick }) => {
  const [activeTab, setActiveTab] = useState('hiring');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`https://vetforcesolutions.com/backend/api/hiring_requests/create`, { ...formData, type: activeTab });
      setIsSuccess(true);
      setFormData({ name: '', email: '', company: '', phone: '', requirements: '' });
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="staffing-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8F262B]/20 to-[#7F070E]/10 border border-teal-500/30 mb-6">
                <Users className="w-4 h-4 text-[#8F262B]" />
                <span className="text-sm text-[#C9C9CD]">Veteran-Owned Staffing Solutions</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold leading-tight mb-6 text-silver-shine" data-testid="staffing-title">
                Elite Talent From{' '}
                <span className="text-gradient">Service to Science</span>{' '}
                and Innovation
              </h1>

              <p className="text-lg mb-8 leading-relaxed text-[#C9C9CD]">
                Veteran-owned staffing solutions specializing in life sciences, technology, and innovation. 
                Connecting world-class professionals with cutting-edge opportunities across pharmaceutical, 
                biotech, engineering, and emerging technology sectors.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-gradient px-8 py-4 text-base flex items-center justify-center gap-2"
                >
                  I'm Hiring Talent
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => {
                    setActiveTab('seeking');
                    document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-outline px-8 py-4 text-base flex items-center justify-center gap-2"
                >
                  I'm Seeking Opportunities
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 pt-8 border-t border-[#2E3446]">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="stat-value text-2xl mb-1">{stat.value}</div>
                    <div className="text-xs text-[#9F999F] uppercase tracking-wider">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden glow-teal">
                <img src={HERO_IMAGE} alt="Life Sciences Laboratory" className="w-full h-auto" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a]/80 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Staffing Solutions */}
      <section className="py-20 section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="section-label">Staffing Solutions</span>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4">
              Comprehensive <span className="text-gradient">Staffing Solutions</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-2xl mx-auto mt-4">
              From service to science and innovation - we provide comprehensive staffing solutions 
              across all employment types with specialized expertise in life sciences and technology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {staffingTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-dark p-6 text-center"
              >
                <div className="icon-box mx-auto mb-4">
                  <type.icon className="w-6 h-6" />
                </div>
                <h3 className="text-silver-shine font-semibold mb-2">{type.title}</h3>
                <p className="text-[#9F999F] text-sm">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Life Sciences Specializations */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Specializations</span>
              <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4 mb-6">
                Life Sciences <span className="text-gradient">Expertise</span>
              </h2>
              <p className="text-[#C9C9CD] mb-8">
                Our expertise in life sciences recruitment covers specialized roles across 
                pharmaceutical, biotech, and medical device industries.
              </p>

              <div className="flex flex-wrap gap-2">
                {lifeScienceRoles.map((role, index) => (
                  <motion.span
                    key={role}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.03 }}
                    className="tech-badge"
                  >
                    {role}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="cta-gradient rounded-2xl p-8 text-center"
            >
              <h3 className="text-2xl font-outfit font-bold text-silver-shine mb-4">
                Ready to Find Your Next Role?
              </h3>
              <p className="text-[#C9C9CD] mb-6">
                Join leading professionals in science and innovation
              </p>
              <button 
                onClick={() => {
                  setActiveTab('seeking');
                  document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-gradient px-6 py-3 inline-flex items-center gap-2"
              >
                Explore Opportunities
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Benefits - For Employers */}
      <section className="py-20 section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="section-label">For Employers</span>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4">
              Partner with <span className="text-gradient">Life Sciences Leaders</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-2xl mx-auto mt-4">
              Access elite validation engineers, clinical researchers, and regulatory specialists 
              who understand the complexities of pharmaceutical and biotech environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-gradient p-6"
              >
                <div className="icon-box w-12 h-12 mb-4">
                  <benefit.icon className="w-6 h-6" />
                </div>
                <h3 className="text-silver-shine font-semibold mb-2">{benefit.title}</h3>
                <p className="text-[#9F999F] text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => {
                setActiveTab('hiring');
                document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-gradient px-8 py-4 inline-flex items-center gap-2"
            >
              Hire Talent
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* For Candidates */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">For Candidates</span>
              <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4 mb-6">
                Advance Your <span className="text-gradient">Professional Career</span>
              </h2>
              <p className="text-[#C9C9CD] mb-8">
                Join elite professionals working on breakthrough pharmaceutical, biotech, 
                and medical device projects with industry-leading companies.
              </p>

              <div className="mb-8">
                <h4 className="text-silver-shine font-semibold mb-4">Popular Roles We Fill:</h4>
                <div className="flex flex-wrap gap-2">
                  {popularRoles.map((role) => (
                    <span key={role} className="tech-badge">{role}</span>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => {
                  setActiveTab('seeking');
                  document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-gradient px-6 py-3 inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Your Resume
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {candidateBenefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-dark p-5"
                >
                  <div className="icon-box w-10 h-10 mb-3">
                    <benefit.icon className="w-5 h-5" />
                  </div>
                  <h4 className="text-silver-shine font-semibold mb-1 text-sm">{benefit.title}</h4>
                  <p className="text-[#9F999F] text-xs">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Verticals */}
      <section className="py-20 section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="section-label">Industries</span>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4">
              Industry <span className="text-gradient">Verticals</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-2xl mx-auto mt-4">
              Our staffing expertise spans multiple industries with deep specialization 
              in life sciences, technology, and emerging sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark p-6"
              >
                <div className="icon-box w-12 h-12 mb-4">
                  <industry.icon className="w-6 h-6" />
                </div>
                <h3 className="text-silver-shine font-semibold mb-1">{industry.title}</h3>
                <p className="text-[#9F999F] text-sm mb-4">{industry.subtitle}</p>
                <div className="flex flex-wrap gap-2">
                  {industry.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-1 rounded bg-[#2E3446] text-[#C9C9CD]">
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="cta-gradient rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine">
                Success <span className="text-gradient">Stories</span>
              </h2>
              <p className="text-[#C9C9CD] mt-4 max-w-xl mx-auto">
                Hear from the professionals and companies who have achieved their goals 
                through our specialized recruitment services.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: '500+', label: 'Professionals Placed', desc: 'Successfully matched specialized talent' },
                { value: '50+', label: 'Partner Companies', desc: 'Leading life sciences organizations' },
                { value: '95%', label: 'Satisfaction Rate', desc: 'Client and candidate satisfaction' },
                { value: '2-3', label: 'Weeks Placement', desc: 'Average time to successful hire' }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="stat-value text-3xl mb-2">{item.value}</div>
                  <div className="text-silver-shine font-semibold mb-1">{item.label}</div>
                  <div className="text-[#C9C9CD] text-sm">{item.desc}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 section-surface border-t border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="section-label">Get Started</span>
              <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mt-4">
                Let's Start the <span className="text-gradient">Conversation</span>
              </h2>
              <p className="text-[#C9C9CD] mt-4">
                Ready to find your next opportunity or hire specialized talent? Get in touch with our team.
              </p>
            </div>

            {/* Tab Buttons */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setActiveTab('hiring')}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'hiring' 
                    ? 'btn-gradient' 
                    : 'bg-[#2E3446] text-[#C9C9CD] hover:bg-[#1B1F2C]'
                }`}
              >
                I'm Hiring
              </button>
              <button
                onClick={() => setActiveTab('seeking')}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'seeking' 
                    ? 'btn-gradient' 
                    : 'bg-[#2E3446] text-[#C9C9CD] hover:bg-[#1B1F2C]'
                }`}
              >
                I'm Job Seeking
              </button>
            </div>

            {isSuccess ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="card-gradient p-8 text-center"
              >
                <div className="w-16 h-16 bg-teal-500/20 border border-teal-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#8F262B]" />
                </div>
                <h3 className="text-xl font-semibold text-silver-shine mb-2">
                  {activeTab === 'hiring' ? 'Requirements Submitted!' : 'Application Submitted!'}
                </h3>
                <p className="text-[#C9C9CD] mb-6">
                  Our team will review your information and get back to you within 24 hours.
                </p>
                <button 
                  onClick={() => setIsSuccess(false)}
                  className="btn-outline px-6 py-2"
                >
                  Submit Another Request
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="card-dark p-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">{activeTab === 'hiring' ? 'Company Name *' : 'Current Company'}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                      required={activeTab === 'hiring'}
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    {activeTab === 'hiring' ? 'Hiring Requirements *' : 'Tell Us About Yourself *'}
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    rows={5}
                    className="form-input resize-none"
                    placeholder={activeTab === 'hiring' 
                      ? 'Describe the roles you need to fill, required skills, and timeline...'
                      : 'Tell us about your experience, skills, and the type of role you are looking for...'
                    }
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-gradient w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      {activeTab === 'hiring' ? 'Submit Requirements' : 'Submit Application'}
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Contact Info */}
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="text-[#8F262B] mb-2">Email Us</div>
                <a href="mailto:info@vetforcesolutions.com" className="text-[#C9C9CD] hover:text-silver-shine transition-colors">
                  Info@vetforcesolutions.com
                </a>
              </div>
              <div className="text-center">
                <div className="text-[#8F262B] mb-2">Call Us</div>
                <a href="tel:440-381-6757" className="text-[#C9C9CD] hover:text-silver-shine transition-colors">
                  (440) 381-6757
                </a>
              </div>
              <div className="text-center">
                <div className="text-[#8F262B] mb-2">Visit Us</div>
                <span className="text-[#C9C9CD]">111 N Orange Ave Suite 800, Orlando FL</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
